import React, { KeyboardEvent } from 'react';

import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { TextField, Autocomplete, TextFieldProps, InputBaseProps } from '@mui/material';
import { useCreatableAutocomplete } from '../hooks/useCreatableAutocomplete';

type LbCreatableAutocompleteProps<
  T extends FieldValues,
  TName extends Path<T>,
  TOption extends Record<string, any>,
  TPrepareAddedOption extends (value: string) => Promise<TOption> | TOption, // need to get error when prepareAddedOption return wrong TOption
> = {
  options: TOption[];
  valueKey?: keyof TOption;
  /** must differ from valueKey: the "Add ..." option overwrites the value key */
  labelKey?: keyof TOption;
  prepareAddedOption: TPrepareAddedOption;
  InputProps?: InputBaseProps;
} & UseControllerProps<T, TName> &
  Pick<TextFieldProps, 'label' | 'variant' | 'size' | 'margin'>;

const LbCreatableAutocomplete = <
  T extends FieldValues,
  TName extends Path<T>,
  TOption extends Record<string, any>,
  TPrepareAddedOption extends (value: string) => Promise<TOption> | TOption,
>({
  control,
  name,
  rules,
  defaultValue = null as PathValue<T, TName>,
  options,
  label,
  variant,
  size,
  margin,
  shouldUnregister = false,
  labelKey = 'label',
  valueKey = 'value',
  prepareAddedOption,
  InputProps,
}: LbCreatableAutocompleteProps<T, TName, TOption, TPrepareAddedOption>) => {
  const {
    field: { onChange, ref, ...field },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const { filterOptions, handleChange, getOptionLabel } = useCreatableAutocomplete({
    prepareAddedOption,
    onChange,
    labelKey,
    valueKey,
  });

  // Enter on typed text creates the option; without this it also submits the form
  const preventEnterSubmit = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const isRequired = !!rules?.required;

  return (
    <Autocomplete
      {...field}
      onChange={handleChange}
      filterOptions={filterOptions}
      size={size}
      freeSolo
      onKeyDown={preventEnterSubmit}
      multiple={false} // narrows onChange value type from string|T|Array<...> to string|T
      selectOnFocus
      clearOnBlur
      options={options}
      getOptionLabel={getOptionLabel}
      slotProps={{
        popper: { className: 'general-style variant-select', placement: 'bottom-start' },
      }}
      renderOption={(props, option) => (
        <li {...props} key={option[valueKey]}>
          {option[labelKey]}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={error?.message}
          error={!!error}
          label={label}
          variant={variant}
          required={isRequired}
          margin={margin}
          inputRef={ref}
          slotProps={{
            input: { ...params.InputProps, ...InputProps },
          }}
        />
      )}
    />
  );
};

export { LbCreatableAutocomplete };
